/**
 * ApplicantPortal.jsx — Applicant Self-Service Portal
 *
 * Public page where applicants can view their application status.
 * Uses a magic-link email auth flow separate from CRM auth.
 *
 * NOTE: The applicant auth backend routes do not exist yet.
 * The following endpoints need to be built:
 *   POST /api/ats/applicants/auth/magic-link        — body: {email}
 *   POST /api/ats/applicants/auth/verify-magic-link  — body: {token}
 *   GET  /api/ats/applicants/portal/me               — returns applicant profile
 *   GET  /api/ats/applicants/portal/applications      — returns applicant's applications
 *
 * Until those routes exist, the login form will display and handle errors gracefully.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import {
    Mail, Loader2, Briefcase, CheckCircle2, Circle, ChevronRight,
    User, BarChart3, Calendar, ArrowRight, ExternalLink, AlertCircle,
    LogOut, Star,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const HEADING_FONT = 'Manrope, system-ui, sans-serif';

// ---------------------------------------------------------------------------
// Stage progress bar — shows coloured dots connected by lines
// ---------------------------------------------------------------------------

const STAGE_COLOURS = {
    completed: '#16a34a',   // green-600
    current: '#2563eb',     // blue-600
    upcoming: '#d6d3d1',    // stone-300
};

function StageProgressBar({ stages = [], currentStage }) {
    if (!stages.length) return null;

    const currentIdx = stages.findIndex(s => s === currentStage);

    return (
        <div className="flex items-center gap-0 w-full overflow-x-auto py-2">
            {stages.map((stage, i) => {
                let status = 'upcoming';
                if (i < currentIdx) status = 'completed';
                else if (i === currentIdx) status = 'current';

                const colour = STAGE_COLOURS[status];
                const isLast = i === stages.length - 1;

                return (
                    <div key={stage} className="flex items-center" style={{ flex: isLast ? '0 0 auto' : '1 1 0' }}>
                        {/* Dot */}
                        <div className="flex flex-col items-center" style={{ minWidth: 24 }}>
                            <div className="relative flex items-center justify-center">
                                {status === 'completed' ? (
                                    <CheckCircle2 className="w-5 h-5" style={{ color: colour }} />
                                ) : status === 'current' ? (
                                    <div className="w-5 h-5 rounded-full border-2 flex items-center justify-center" style={{ borderColor: colour }}>
                                        <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: colour }} />
                                    </div>
                                ) : (
                                    <Circle className="w-5 h-5" style={{ color: colour }} />
                                )}
                            </div>
                            <span className="text-[10px] mt-1 text-stone-500 whitespace-nowrap text-center leading-tight max-w-[72px] truncate">
                                {stage}
                            </span>
                        </div>
                        {/* Connector line */}
                        {!isLast && (
                            <div
                                className="flex-1 h-0.5 mx-1"
                                style={{
                                    backgroundColor: i < currentIdx ? STAGE_COLOURS.completed : STAGE_COLOURS.upcoming,
                                    minWidth: 16,
                                }}
                            />
                        )}
                    </div>
                );
            })}
        </div>
    );
}

// ---------------------------------------------------------------------------
// Profile completeness bar
// ---------------------------------------------------------------------------

function CompletenessBar({ percent = 0 }) {
    return (
        <div className="flex items-center gap-3">
            <div className="flex-1 h-2 bg-stone-200 rounded-full overflow-hidden">
                <div
                    className="h-full rounded-full transition-all duration-500"
                    style={{
                        width: `${Math.min(100, Math.max(0, percent))}%`,
                        backgroundColor: percent >= 80 ? '#16a34a' : percent >= 50 ? '#ca8a04' : '#dc2626',
                    }}
                />
            </div>
            <span className="text-xs font-medium text-stone-500 whitespace-nowrap">{percent}% complete</span>
        </div>
    );
}

// ---------------------------------------------------------------------------
// Application card
// ---------------------------------------------------------------------------

function ApplicationCard({ application }) {
    const {
        job_title = 'Untitled Position',
        department,
        stages = [],
        current_stage = '',
        match_score,
        applied_at,
        updated_at,
    } = application;

    return (
        <div className="bg-white rounded-xl border border-stone-200 p-5 hover:shadow-md transition-shadow">
            <div className="flex items-start justify-between gap-3 mb-3">
                <div>
                    <h3 className="font-semibold text-stone-900" style={{ fontFamily: HEADING_FONT }}>
                        {job_title}
                    </h3>
                    {department && (
                        <p className="text-xs text-stone-500 mt-0.5">{department}</p>
                    )}
                </div>
                {match_score != null && (
                    <span className="flex items-center gap-1 text-xs font-medium px-2 py-1 rounded-full bg-blue-50 text-blue-700 whitespace-nowrap">
                        <Star className="w-3 h-3" /> {match_score}% match
                    </span>
                )}
            </div>

            {/* Stage progress */}
            {stages.length > 0 && (
                <StageProgressBar stages={stages} currentStage={current_stage} />
            )}

            {/* Current stage label */}
            <div className="flex items-center justify-between mt-3 pt-3 border-t border-stone-100">
                <span className="inline-flex items-center gap-1.5 text-xs font-medium text-blue-700 bg-blue-50 px-2.5 py-1 rounded-full">
                    <Circle className="w-2.5 h-2.5 fill-current" /> {current_stage || 'Applied'}
                </span>
                <div className="flex items-center gap-3 text-[11px] text-stone-400">
                    {applied_at && (
                        <span className="flex items-center gap-1">
                            <Calendar className="w-3 h-3" /> Applied {formatDate(applied_at)}
                        </span>
                    )}
                    {updated_at && updated_at !== applied_at && (
                        <span className="flex items-center gap-1">
                            Updated {formatDate(updated_at)}
                        </span>
                    )}
                </div>
            </div>
        </div>
    );
}

function formatDate(iso) {
    if (!iso) return '';
    try {
        return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    } catch {
        return iso;
    }
}

// ---------------------------------------------------------------------------
// Main portal component
// ---------------------------------------------------------------------------

export default function ApplicantPortal() {
    const [searchParams] = useSearchParams();

    // Auth state
    const [email, setEmail] = useState('');
    const [linkSent, setLinkSent] = useState(false);
    const [sending, setSending] = useState(false);
    const [authError, setAuthError] = useState(null);
    const [token, setToken] = useState(() => localStorage.getItem('applicant_token') || null);

    // Data state
    const [applicant, setApplicant] = useState(null);
    const [applications, setApplications] = useState([]);
    const [loadingData, setLoadingData] = useState(false);
    const [dataError, setDataError] = useState(null);

    // ---------------------------------------------------------------------------
    // On mount, check for magic-link token in URL query params
    // ---------------------------------------------------------------------------
    useEffect(() => {
        const urlToken = searchParams.get('token');
        if (urlToken) {
            verifyToken(urlToken);
        } else if (token) {
            fetchPortalData(token);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // ---------------------------------------------------------------------------
    // Send magic link
    // ---------------------------------------------------------------------------
    const sendMagicLink = async (e) => {
        e.preventDefault();
        if (!email.trim()) return;
        setSending(true);
        setAuthError(null);
        try {
            await axios.post(`${API}/ats/applicants/auth/magic-link`, { email: email.trim().toLowerCase() });
            setLinkSent(true);
        } catch (err) {
            const msg = err.response?.data?.detail || err.response?.data?.message || 'Unable to send login link. Please try again.';
            setAuthError(msg);
        } finally {
            setSending(false);
        }
    };

    // ---------------------------------------------------------------------------
    // Verify token from magic link
    // ---------------------------------------------------------------------------
    const verifyToken = async (t) => {
        setLoadingData(true);
        setDataError(null);
        try {
            const res = await axios.post(`${API}/ats/applicants/auth/verify-magic-link`, { token: t });
            const accessToken = res.data.access_token || res.data.token || t;
            localStorage.setItem('applicant_token', accessToken);
            setToken(accessToken);
            await fetchPortalData(accessToken);
        } catch (err) {
            setDataError('Invalid or expired login link. Please request a new one.');
            localStorage.removeItem('applicant_token');
            setToken(null);
            setLoadingData(false);
        }
    };

    // ---------------------------------------------------------------------------
    // Fetch portal data (profile + applications)
    // ---------------------------------------------------------------------------
    const fetchPortalData = useCallback(async (t) => {
        setLoadingData(true);
        setDataError(null);
        const headers = { Authorization: `Bearer ${t}` };
        try {
            const [profileRes, appsRes] = await Promise.all([
                axios.get(`${API}/ats/applicants/portal/me`, { headers }),
                axios.get(`${API}/ats/applicants/portal/applications`, { headers }),
            ]);
            setApplicant(profileRes.data);
            setApplications(Array.isArray(appsRes.data) ? appsRes.data : appsRes.data?.data || []);
        } catch (err) {
            if (err.response?.status === 401 || err.response?.status === 403) {
                localStorage.removeItem('applicant_token');
                setToken(null);
                setDataError('Session expired. Please log in again.');
            } else {
                setDataError('Unable to load your portal data. The applicant portal backend is not yet available.');
            }
        } finally {
            setLoadingData(false);
        }
    }, []);

    // ---------------------------------------------------------------------------
    // Logout
    // ---------------------------------------------------------------------------
    const logout = () => {
        localStorage.removeItem('applicant_token');
        setToken(null);
        setApplicant(null);
        setApplications([]);
        setLinkSent(false);
        setEmail('');
        setDataError(null);
        setAuthError(null);
    };

    // ---------------------------------------------------------------------------
    // Render: loading spinner
    // ---------------------------------------------------------------------------
    if (loadingData) {
        return (
            <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#FAFAF5' }}>
                <div className="text-center">
                    <Loader2 className="w-8 h-8 animate-spin text-stone-400 mx-auto mb-3" />
                    <p className="text-sm text-stone-500">Loading your portal...</p>
                </div>
            </div>
        );
    }

    // ---------------------------------------------------------------------------
    // Render: authenticated dashboard
    // ---------------------------------------------------------------------------
    if (token && applicant) {
        const profilePercent = applicant.profile_completeness ?? applicant.completeness ?? 60;

        return (
            <div className="min-h-screen" style={{ backgroundColor: '#FAFAF5' }}>
                {/* Header */}
                <div className="bg-stone-900 text-white">
                    <div className="max-w-4xl mx-auto px-6 py-10">
                        <div className="flex items-center justify-between mb-4">
                            <div className="flex items-center gap-3">
                                <div className="w-12 h-12 rounded-full bg-stone-700 flex items-center justify-center text-lg font-bold" style={{ fontFamily: HEADING_FONT }}>
                                    {(applicant.name || applicant.email || '?')[0].toUpperCase()}
                                </div>
                                <div>
                                    <h1 className="text-xl font-bold" style={{ fontFamily: HEADING_FONT }}>
                                        Welcome back, {applicant.name || applicant.email}
                                    </h1>
                                    <p className="text-stone-400 text-sm">{applicant.email}</p>
                                </div>
                            </div>
                            <button onClick={logout} className="text-stone-400 hover:text-white transition-colors text-sm flex items-center gap-1.5">
                                <LogOut className="w-4 h-4" /> Sign out
                            </button>
                        </div>
                        <div className="max-w-md">
                            <CompletenessBar percent={profilePercent} />
                        </div>
                    </div>
                </div>

                <div className="max-w-4xl mx-auto px-6 py-8 space-y-8">
                    {/* My Applications */}
                    <section>
                        <div className="flex items-center justify-between mb-4">
                            <h2 className="text-lg font-semibold text-stone-900" style={{ fontFamily: HEADING_FONT }}>
                                My Applications
                            </h2>
                            <span className="text-xs text-stone-400">{applications.length} application{applications.length !== 1 ? 's' : ''}</span>
                        </div>

                        {applications.length === 0 ? (
                            <div className="bg-white rounded-xl border border-stone-200 p-10 text-center">
                                <Briefcase className="w-10 h-10 text-stone-300 mx-auto mb-3" />
                                <p className="text-stone-500 text-sm mb-4">You have not applied to any positions yet.</p>
                                <Link
                                    to="/careers"
                                    className="inline-flex items-center gap-2 px-4 py-2 bg-stone-900 text-white text-sm font-medium rounded-lg hover:bg-stone-800 transition-colors"
                                >
                                    Browse Open Positions <ArrowRight className="w-4 h-4" />
                                </Link>
                            </div>
                        ) : (
                            <div className="space-y-3">
                                {applications.map((app, i) => (
                                    <ApplicationCard key={app.application_id || i} application={app} />
                                ))}
                            </div>
                        )}
                    </section>

                    {/* My Profile */}
                    <section>
                        <h2 className="text-lg font-semibold text-stone-900 mb-4" style={{ fontFamily: HEADING_FONT }}>
                            My Profile
                        </h2>
                        <div className="bg-white rounded-xl border border-stone-200 p-5">
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                                <ProfileField icon={User} label="Name" value={applicant.name} />
                                <ProfileField icon={Mail} label="Email" value={applicant.email} />
                                {applicant.phone && <ProfileField icon={User} label="Phone" value={applicant.phone} />}
                                {applicant.location && <ProfileField icon={User} label="Location" value={applicant.location} />}
                            </div>
                            {profilePercent < 100 && (
                                <div className="mt-4 pt-4 border-t border-stone-100 flex items-center justify-between">
                                    <p className="text-xs text-stone-500">Complete your profile to improve your match scores.</p>
                                    <button className="text-xs font-medium text-blue-700 hover:text-blue-800 flex items-center gap-1">
                                        Update Profile <ChevronRight className="w-3 h-3" />
                                    </button>
                                </div>
                            )}
                        </div>
                    </section>

                    {/* Quick link to careers */}
                    <section className="bg-white rounded-xl border border-stone-200 p-5 flex items-center justify-between">
                        <div>
                            <h3 className="font-medium text-stone-900 text-sm" style={{ fontFamily: HEADING_FONT }}>Looking for more opportunities?</h3>
                            <p className="text-xs text-stone-500 mt-0.5">Browse all open positions and find your next role.</p>
                        </div>
                        <Link
                            to="/careers"
                            className="inline-flex items-center gap-2 px-4 py-2 bg-stone-900 text-white text-sm font-medium rounded-lg hover:bg-stone-800 transition-colors whitespace-nowrap"
                        >
                            Open Positions <ExternalLink className="w-4 h-4" />
                        </Link>
                    </section>

                    {/* Footer */}
                    <div className="text-center py-6 border-t border-stone-200">
                        <p className="text-xs text-stone-400">Powered by NurtureCRM</p>
                    </div>
                </div>
            </div>
        );
    }

    // ---------------------------------------------------------------------------
    // Render: login / magic-link flow
    // ---------------------------------------------------------------------------
    return (
        <div className="min-h-screen flex flex-col" style={{ backgroundColor: '#FAFAF5' }}>
            {/* Minimal header */}
            <div className="bg-stone-900 text-white">
                <div className="max-w-4xl mx-auto px-6 py-10 text-center">
                    <Briefcase className="w-10 h-10 text-stone-400 mx-auto mb-3" />
                    <h1 className="text-2xl font-bold" style={{ fontFamily: HEADING_FONT }}>
                        Applicant Portal
                    </h1>
                    <p className="text-stone-400 text-sm mt-1">Track your applications and manage your profile</p>
                </div>
            </div>

            <div className="flex-1 flex items-start justify-center px-6 py-12">
                <div className="w-full max-w-md">
                    {/* Error from data load (e.g. expired token) */}
                    {dataError && (
                        <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-xl flex items-start gap-3">
                            <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                            <p className="text-sm text-amber-800">{dataError}</p>
                        </div>
                    )}

                    <div className="bg-white rounded-xl border border-stone-200 p-6 shadow-sm">
                        {linkSent ? (
                            /* Success — check email */
                            <div className="text-center py-4">
                                <div className="w-14 h-14 rounded-full bg-green-50 flex items-center justify-center mx-auto mb-4">
                                    <Mail className="w-7 h-7 text-green-600" />
                                </div>
                                <h2 className="text-lg font-semibold text-stone-900 mb-2" style={{ fontFamily: HEADING_FONT }}>
                                    Check your email
                                </h2>
                                <p className="text-sm text-stone-500 mb-1">
                                    We sent a login link to
                                </p>
                                <p className="text-sm font-medium text-stone-700 mb-4">{email}</p>
                                <p className="text-xs text-stone-400 mb-6">
                                    The link will expire in 15 minutes. Check your spam folder if you don't see it.
                                </p>
                                <button
                                    onClick={() => { setLinkSent(false); setAuthError(null); }}
                                    className="text-sm text-stone-500 hover:text-stone-700 underline underline-offset-2"
                                >
                                    Use a different email
                                </button>
                            </div>
                        ) : (
                            /* Email input form */
                            <form onSubmit={sendMagicLink}>
                                <div className="text-center mb-6">
                                    <h2 className="text-lg font-semibold text-stone-900" style={{ fontFamily: HEADING_FONT }}>
                                        Sign in to your portal
                                    </h2>
                                    <p className="text-sm text-stone-500 mt-1">
                                        Enter your email and we'll send you a login link.
                                    </p>
                                </div>

                                {authError && (
                                    <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
                                        <AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" />
                                        <p className="text-sm text-red-700">{authError}</p>
                                    </div>
                                )}

                                <label className="block text-sm font-medium text-stone-700 mb-1.5">Email address</label>
                                <div className="relative mb-4">
                                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4.5 h-4.5 text-stone-400" />
                                    <input
                                        type="email"
                                        required
                                        value={email}
                                        onChange={e => setEmail(e.target.value)}
                                        placeholder="you@example.com"
                                        className="w-full pl-10 pr-4 py-2.5 text-sm border border-stone-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-stone-900/10 focus:border-stone-300"
                                    />
                                </div>

                                <button
                                    type="submit"
                                    disabled={sending || !email.trim()}
                                    className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-stone-900 text-white text-sm font-medium rounded-lg hover:bg-stone-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                >
                                    {sending ? (
                                        <>
                                            <Loader2 className="w-4 h-4 animate-spin" /> Sending...
                                        </>
                                    ) : (
                                        <>
                                            Send Magic Link <ArrowRight className="w-4 h-4" />
                                        </>
                                    )}
                                </button>
                            </form>
                        )}
                    </div>

                    {/* Browse positions link */}
                    <div className="text-center mt-6">
                        <Link to="/careers" className="text-sm text-stone-500 hover:text-stone-700 inline-flex items-center gap-1.5">
                            <Briefcase className="w-4 h-4" /> Browse open positions
                        </Link>
                    </div>
                </div>
            </div>

            {/* Footer */}
            <div className="text-center py-6 border-t border-stone-200">
                <p className="text-xs text-stone-400">Powered by NurtureCRM</p>
            </div>
        </div>
    );
}

// ---------------------------------------------------------------------------
// Small helper component
// ---------------------------------------------------------------------------

function ProfileField({ icon: Icon, label, value }) {
    if (!value) return null;
    return (
        <div className="flex items-center gap-2">
            <Icon className="w-4 h-4 text-stone-400 flex-shrink-0" />
            <div>
                <p className="text-xs text-stone-400">{label}</p>
                <p className="text-stone-700">{value}</p>
            </div>
        </div>
    );
}
